import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, GeneralNewsItem, NewsCategory, categoryOf } from '../api';
import { useAuth } from '../auth/AuthContext';
import { watchesApi } from '../watchesApi';
import { MatchTerm, buildTerms, matchedLabels } from '../watchMatch';
import { newsThumbClass, useSettings } from '../settings/SettingsContext';

const DEFAULT_DAYS = 30;
const PAGE_SIZE = 12;
const FADE_MS = 200;

/** 'mine' is not a category -- it filters by the user's watch list instead. */
type FeedFilter = NewsCategory | 'mine';

const FILTERS: { key: FeedFilter; label: string }[] = [
  { key: 'news', label: 'News' },
  { key: 'video', label: 'Videos' },
  { key: 'social', label: 'Social Media' },
  { key: 'all', label: 'All' },
];

const MINE: { key: FeedFilter; label: string } = { key: 'mine', label: 'My Watches' };

export default function GeneralNews() {
  // `selected` drives the chip highlight (updates instantly); `filter` drives
  // the data load and is swapped only after the fade-out, so the list content
  // changes while it is invisible.
  const [selected, setSelected] = useState<FeedFilter>('news');
  const [filter, setFilter] = useState<FeedFilter>('news');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [news, setNews] = useState<GeneralNewsItem[] | null>(null);
  const [visible, setVisible] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const fadeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { session } = useAuth();
  // Set from the nav's display settings, so it applies to every page that
  // grows a thumbnail rather than to this list alone.
  const { thumbs } = useSettings();
  const [terms, setTerms] = useState<MatchTerm[] | null>(null);
  // Headlines matching the watch list in the same window the other filters
  // use, so the badge is comparable to what those chips would show.
  const [mineCount, setMineCount] = useState<number | null>(null);

  const mine = filter === 'mine';

  // A query searches the whole dataset; so does the watch filter, since
  // matches are sparse and a 30-day window would hide most of them.
  const searching = debouncedQuery.trim() !== '';
  const days = searching || mine ? undefined : DEFAULT_DAYS;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 200);
    return () => clearTimeout(t);
  }, [query]);

  // Load the watch list once signed in, so the chip is ready when clicked.
  useEffect(() => {
    if (!session) {
      setTerms(null);
      return;
    }
    let cancelled = false;
    watchesApi
      .list()
      .then(ws => {
        if (!cancelled) setTerms(buildTerms(ws));
      })
      .catch(() => {
        if (!cancelled) setTerms([]);
      });
    return () => {
      cancelled = true;
    };
  }, [session]);

  // Count matches for the chip badge. This is a filter over the already-loaded
  // news array (api.ts caches it), so it costs no extra request.
  useEffect(() => {
    if (terms === null) return;
    if (terms.length === 0) {
      setMineCount(0);
      return;
    }
    let cancelled = false;
    api
      .getGeneralNews({ days: DEFAULT_DAYS, category: 'all', matchTerms: terms })
      .then(items => {
        if (!cancelled) setMineCount(items.length);
      })
      .catch(() => {
        if (!cancelled) setMineCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [terms]);

  // Signing out while viewing "My Watches" would otherwise strand an empty list.
  useEffect(() => {
    if (!session && (filter === 'mine' || selected === 'mine')) {
      setSelected('news');
      setFilter('news');
    }
  }, [session, filter, selected]);

  const buildOpts = useCallback(
    (offset: number) => ({
      days,
      limit: PAGE_SIZE,
      offset,
      category: (mine ? 'all' : filter) as NewsCategory,
      query: debouncedQuery,
      matchTerms: mine ? terms ?? [] : undefined,
    }),
    [days, mine, filter, debouncedQuery, terms]
  );

  useEffect(() => {
    // Wait for the watch list before filtering by it, or the first pass would
    // match nothing and flash an empty state.
    if (mine && terms === null) return;

    let cancelled = false;
    api.getGeneralNews(buildOpts(0)).then(page => {
      if (cancelled) return;
      setNews(page);
      setHasMore(page.length === PAGE_SIZE);
      requestAnimationFrame(() => setVisible(true));
    });
    return () => {
      cancelled = true;
    };
  }, [buildOpts, mine, terms]);

  useEffect(() => () => {
    if (fadeTimer.current) clearTimeout(fadeTimer.current);
  }, []);

  function changeFilter(next: FeedFilter) {
    if (next === selected) return;
    setSelected(next); // instant chip feedback
    setVisible(false); // fade the current list out
    if (fadeTimer.current) clearTimeout(fadeTimer.current);
    fadeTimer.current = setTimeout(() => setFilter(next), FADE_MS);
  }

  async function loadMore() {
    if (!news) return;
    setLoadingMore(true);
    const nextPage = await api.getGeneralNews(buildOpts(news.length));
    setNews([...news, ...nextPage]);
    setHasMore(nextPage.length === PAGE_SIZE);
    setLoadingMore(false);
  }

  const noWatches = mine && terms !== null && terms.length === 0;
  // terms === null means the list is still loading -- show the chip without a
  // badge rather than flashing the empty-list variant at someone who has one.
  const watchesEmpty = terms !== null && terms.length === 0;

  function emptyMessage() {
    if (searching) return `No results for "${debouncedQuery.trim()}".`;
    if (noWatches) return null; // handled separately, needs a link
    if (mine) return 'No headlines mention anything on your watch list yet.';
    return `No news in the last ${DEFAULT_DAYS} days.`;
  }

  return (
    <>
      <div className="page-header">
        <h1>Watch News</h1>
      </div>
      <p style={{ color: '#999', fontSize: '0.9rem', marginTop: '-1.5rem', marginBottom: '1.5rem' }}>
        The latest from watch news sites and forums.
      </p>

      <div className="list-controls">
        <div className="filter-chips">
          {/* "My Watches" leads the row and is divided off from the three
              category chips: it is a personal scope that cuts across all of
              them, not a fourth category. */}
          {session &&
            (watchesEmpty ? (
              <Link to="/watches" className="chip chip-mine chip-mine-empty">
                <span aria-hidden="true">+</span> Add watches
              </Link>
            ) : (
              <button
                className={`chip chip-mine${selected === MINE.key ? ' chip-mine-active' : ''}`}
                onClick={() => changeFilter(MINE.key)}
              >
                {MINE.label}
                {/* Brand-level matching means common brands run into the
                    hundreds, so cap the label to keep the badge a badge. */}
                {mineCount !== null && (
                  <span className="chip-count">
                    {mineCount > 99 ? '99+' : mineCount}
                  </span>
                )}
              </button>
            ))}
          {session && <span className="chip-divider" aria-hidden="true" />}

          {FILTERS.map(f => (
            <button
              key={f.key}
              className={`chip${selected === f.key ? ' chip-active' : ''}`}
              onClick={() => changeFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="search-box">
          <input
            type="text"
            placeholder="Search by keyword…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            aria-label="Search news"
          />
          {query && (
            <button className="btn btn-outline" onClick={() => setQuery('')}>
              Clear
            </button>
          )}
        </div>
      </div>

      <div className={`card fade-container${visible ? '' : ' is-hidden'}`}>
        {news === null ? (
          <p style={{ padding: '1rem', color: '#999', fontSize: '0.9rem' }}>Loading...</p>
        ) : noWatches ? (
          <p style={{ padding: '1rem', color: '#999', fontSize: '0.9rem' }}>
            Your watch list is empty. <Link to="/watches">Add a watch</Link> to filter news by it.
          </p>
        ) : news.length === 0 ? (
          <p style={{ padding: '1rem', color: '#999', fontSize: '0.9rem' }}>{emptyMessage()}</p>
        ) : (
          news.map(item => {
            const hits = mine && terms ? matchedLabels(item.title, terms) : [];
            const cat = categoryOf(item);
            // 'off' drops the thumbnail column entirely, placeholders
            // included -- a row of empty grey squares is the noise the setting
            // exists to remove.
            const thumb =
              thumbs === 'off' ? null : item.image ? (
                <img
                  className={newsThumbClass(thumbs)}
                  src={item.image}
                  alt=""
                  loading="lazy"
                  onError={e => {
                    e.currentTarget.classList.add('news-thumb-empty');
                    e.currentTarget.classList.remove('news-thumb-blur');
                    e.currentTarget.removeAttribute('src');
                  }}
                />
              ) : cat === 'news' ? (
                <div className="news-thumb news-thumb-empty" aria-hidden="true" />
              ) : null;
            return (
              <div key={item.id} className="news-item">
                {/* The thumbnail links to the same place as the headline --
                    it reads as clickable (especially with a play badge on it),
                    so it must be. Hidden from screen readers and skipped in the
                    tab order: it is a duplicate of the headline link, not a
                    second destination.

                    The play badge marks video items, which are otherwise
                    indistinguishable from articles in the mixed views. */}
                {thumb && (
                  <a
                    className="news-thumb-link"
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                    aria-hidden="true"
                    tabIndex={-1}
                  >
                    {thumb}
                    {cat === 'video' && (
                      <span className="news-play" aria-hidden="true">
                        <svg viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </span>
                    )}
                  </a>
                )}
                <div className="news-text">
                  <a href={item.url} target="_blank" rel="noreferrer">{item.title}</a>
                  <div className="news-meta">
                    {item.source ? item.source : ''}
                    {item.published_at ? ` · ${new Date(item.published_at).toLocaleDateString()}` : ''}
                  </div>
                  {hits.length > 0 && (
                    <div className="news-hits">
                      {hits.map(h => (
                        <span key={h} className="tag">{h}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {news !== null && news.length > 0 && hasMore && (
        <button
          className="btn btn-outline mt-2"
          onClick={loadMore}
          disabled={loadingMore}
        >
          {loadingMore ? 'Loading...' : 'Show older'}
        </button>
      )}
    </>
  );
}
